import classnames from 'classnames';
import {useMemo, useState} from 'react';

import {useAnnotationModeStore} from '../store/AnnotationModeStore';
import {isAnchoredInLang} from '../engine/annotationAnchors';
import {
  selectIndicatorColor,
  selectNotedMarksInOrder,
  selectSupplementBorderColor,
  selectVisibleRegionAnnotations
} from '../store/annotationSelectors';
import type {Annotation, HighlightColor} from '../types';
import {SUPPLEMENT_BORDER_CLASS_BY_COLOR, SUPPLEMENT_MARKED_CLASS} from '../shared/supplementCard';

/**
 * What a supplement's marks make of its card, one mark or many. `regionRef` names the
 * box the edge paints on, held as state because a ref is still null on the mounting
 * pass and nothing re-renders when the marks arrive with it.
 */
export function useSupplementCard(
  regionId: string,
  lang: string | null = null
): {
  regionRef: (element: HTMLDivElement | null) => void;
  /** The card's box once it has attached; null on the mounting pass. */
  region: HTMLElement | null;
  /** The marks anchored in the language on screen, in the order they were written. */
  annotations: Annotation[];
  cardClassName: string;
  /** The marks carrying a note, in any language, in the order they read. */
  notedMarks: Annotation[];
  /** The note indicator's fill, from the noted marks painted in the language on screen. */
  indicatorColor: HighlightColor;
} {
  const [region, setRegion] = useState<HTMLElement | null>(null);
  const storedAnnotations = useAnnotationModeStore((state) =>
    selectVisibleRegionAnnotations(state, regionId)
  );
  // The edge follows the paint, so only marks in the language on screen color it.
  const annotations = useMemo(
    () => storedAnnotations.filter(({anchor}) => isAnchoredInLang(anchor, lang)),
    [storedAnnotations, lang]
  );
  const borderColor = useMemo(() => selectSupplementBorderColor(annotations), [annotations]);
  const notedMarks = useMemo(() => selectNotedMarksInOrder(storedAnnotations), [storedAnnotations]);
  const indicatorColor = useMemo(
    () => selectIndicatorColor(selectNotedMarksInOrder(annotations)),
    [annotations]
  );

  return {
    regionRef: setRegion,
    region,
    annotations,
    cardClassName: classnames(
      borderColor && [SUPPLEMENT_MARKED_CLASS, SUPPLEMENT_BORDER_CLASS_BY_COLOR[borderColor]]
    ),
    notedMarks,
    indicatorColor
  };
}
